import React from 'react';
import axios from 'axios';
import { getUrl } from '../../config';
import {
  BookOpen,
} from "react-feather";
import SettingsIcon from '@mui/icons-material/Settings';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FactoryIcon from '@mui/icons-material/Factory';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import HomeIcon from '@mui/icons-material/Home';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import BuildIcon from '@mui/icons-material/Build';
import ChatIcon from '@mui/icons-material/Chat';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import PeopleIcon from '@mui/icons-material/People';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CodeIcon from '@mui/icons-material/Code';
import { useUnreadMessages } from '../../contexts/UnreadMessagesContext';
import { useActiveConversation } from '../../contexts/ActiveConversationContext';
import useAuth from '../../hooks/useAuth';
import { useLocation } from 'react-router-dom';

const useDashboardItems = () => {
  const { totalUnreadCount, unreadByConversation } = useUnreadMessages();
  const { activeConversationId } = useActiveConversation();
  const { user } = useAuth();
  const location = useLocation();
  const [hasUrgency, setHasUrgency] = React.useState(false);
  const [hasVoitureMachineUrgency, setHasVoitureMachineUrgency] = React.useState(false);
  const [voitureMachineUrgencyCount, setVoitureMachineUrgencyCount] = React.useState(0);

  React.useEffect(() => {
    let active = true;
    const checkUrgency = async () => {
      try {
        if (user) {
          const response = await axios.get(`${getUrl()}/api/fiche-personnel-has-urgency`);
          if (active && response.data && response.data.status === 'success') {
            setHasUrgency(!!response.data.hasUrgency);
          }
        }
      } catch (error) {
        console.error("Erreur lors de la vérification de l'urgence RH:", error);
      }
    };

    checkUrgency();

    const handleCheck = () => {
      checkUrgency();
    };
    window.addEventListener('rh-urgency-check', handleCheck);

    const interval = setInterval(checkUrgency, 30000);

    return () => {
      active = false;
      clearInterval(interval);
      window.removeEventListener('rh-urgency-check', handleCheck);
    };
  }, [user, location.pathname]);

  React.useEffect(() => {
    let active = true;
    const checkVoitureMachineUrgency = async () => {
      try {
        if (user) {
          const response = await axios.get(`${getUrl()}/api/voiture-machine-has-urgency`);
          if (active && response.data && response.data.status === 'success') {
            setHasVoitureMachineUrgency(!!response.data.hasUrgency);
            setVoitureMachineUrgencyCount(response.data.count || (response.data.hasUrgency ? 1 : 0));
          }
        }
      } catch (error) {
        console.error("Erreur lors de la vérification de l'urgence Véhicule/Machine:", error);
      }
    };

    checkVoitureMachineUrgency();

    const handleVMCheck = () => {
      checkVoitureMachineUrgency();
    };
    window.addEventListener('voiture-machine-urgency-check', handleVMCheck);

    const interval = setInterval(checkVoitureMachineUrgency, 30000);

    return () => {
      active = false;
      clearInterval(interval);
      window.removeEventListener('voiture-machine-urgency-check', handleVMCheck);
    };
  }, [user, location.pathname]);

  // Calculer le nombre de messages non lus en excluant la conversation active
  const badgeCount = React.useMemo(() => {
    return Object.entries(unreadByConversation)
      .filter(([conversationId]) => conversationId !== activeConversationId?.toString())
      .reduce((sum, [, count]) => sum + count, 0);
  }, [unreadByConversation, activeConversationId]);

  const pagesSection = [
    {
      href: "/",
      icon: HomeIcon,
      title: "Dashboard",
      permission: "dashboard_view"
    },

    {
      href: "/pages",
      icon: BookOpen,
      title: "Gestion",
      highlightRed: hasVoitureMachineUrgency,
      badge: voitureMachineUrgencyCount > 0 ? String(voitureMachineUrgencyCount) : null,
      children: [
        {
          href: "/gestion/opportunity",
          title: "Opportunité",
          permission: "devis_view"
        },
        {
          href: "/gestion/devis",
          title: "Devis",
          permission: "devis_view"
        },
        {
          href: "/gestion/commande",
          title: "Commande",
          permission: "commande_view"
        },
        {
          href: "/gestion/preparation",
          title: "Préparation commande",
          permission: "bon_view"
        },
        {
          title: "Bon de Livraison",
          children: [
            {
              href: "/gestion/bon",
              permission: "bon_view",
              title: "Bon Simple",
            },
            {
              href: "/gestion/bon/multiple",
              permission: "bon_view",
              title: "Bon Multiple",
            },
          ]
        },
        {
          href: "/gestion/facture",
          title: "Facture",
          permission: "facture_view"
        },
        {
          href: "/gestion/facture/auto",
          title: "Facture Auto",
          permission: "facture_view"
        },
        {
          href: "/gestion/achat",
          title: "Achat",
          permission: "achat_view"
        },
        {
          href: "/gestion/container",
          title: "Pré-réception",
          permission: "reception_view"
        },
        {
          href: "/gestion/reception",
          title: "Reception",
          permission: "reception_view"
        },
        {
          href: "/gestion/voiture-machine",
          title: "Véhicule et Machine",
          permission: "reception_view",
          highlightRed: hasVoitureMachineUrgency,
          badge: voitureMachineUrgencyCount > 0 ? String(voitureMachineUrgencyCount) : null
        },
      ],
    },
    {
      href: "/file",
      icon: FactoryIcon,
      title: "Fichier de Base",
      children: [
        {
          href: "/file/client",
          title: "Clients",
          permission: "client_view"
        },
        {
          href: "/file/matiere",
          title: "Matières",
          permission: "matiere_view"
        },
        {
          href: "/file/article",
          title: "Articles",
          permission: "article_view"
        },
        {
          href: "/file/fournisseur",
          title: "Fournisseurs",
          permission: "fournisseur_view"
        },
        {
          href: "/file/stock",
          title: "Stocks",
          permission: "stock_view"
        },
        {
          href: "/file/etat-stock",
          title: "Etat stock",
          permission: "stock_view"
        },
        {
          href: "/file/debit",
          title: "Débits",
          permission: "stock_view"
        },
        {
          href: "/file/carrousel",
          title: "Carrousel",
          permission: "stock_view"
        },
      ],
    },
    {
      icon: BusinessCenterIcon,
      title: "CRM",
      permission: "qualite_view",
      children: [
        {
          href: "/rh/suivi-client",
          title: "Suivi Client",
          permission: "qualite_view"
        },
      ],
    },
    {
      icon: BuildIcon,
      title: "Qualité",
      permission: "qualite_view",
      children: [
        {
          href: "/gestion/documentation",
          title: "Gestion documentaire",
          permission: "dashboard_view"
        },
        {
          href: "/rh/non-conformite",
          title: "Gestion non conformités",
          permission: "qualite_view"
        },
        {
          href: "/rh/audit",
          title: "Audits",
          permission: "qualite_view"
        },
        {
          href: "/rh/plan-action",
          title: "Plan d'action",
          permission: "qualite_view"
        },
        {
          title: "Opportunités et Risques",
          permission: "qualite_view",
          children: [
            {
              href: "/rh/opportunites-risques",
              title: "Opportunités et Risques",
              permission: "qualite_view"
            },
            {
              href: "/rh/opportunites-risques/parametres/processus",
              title: "Processus",
              permission: "qualite_view"
            },
          ]
        },
        {
          href: "/rh/revue-direction",
          title: "Revue de direction",
          permission: "qualite_view"
        },
      ],
    },
    {
      icon: PeopleIcon,
      title: "Ressource Humaine",
      permission: "rh_view",
      highlightRed: hasUrgency,
      children: [
        {
          href: "/rh/organigramme",
          title: "Organigramme",
          permission: "rh_view"
        },
        {
          href: "/rh/notes",
          title: "Notes",
          permission: "rh_view"
        },

        {
          href: "/rh/fiche-personnel",
          title: "Suivi Personnel",
          permission: "rh_view",
          highlightRed: hasUrgency
        },
        {
          href: "/rh/entretien-professionnel",
          title: "Entretien professionnel",
          permission: "rh_view"
        },
        {
          href: "/rh/formation",
          title: "Formations",
          permission: "rh_view"
        },
        {
          href: "/rh/recrutement",
          title: "Recrutements",
          permission: "rh_view"
        },
        {
          title: "Paramètres RH",
          permission: "rh_view",
          children: [
            {
              href: "/rh/fiche-poste",
              title: "Fiche de poste",
              permission: "rh_view"
            },
            {
              href: "/rh/habilitation",
              title: "Habilitation",
              permission: "rh_view"
            },
            {
              href: "/rh/competences",
              title: "Compétence",
              permission: "rh_view"
            },
          ]
        },
      ],
    },
    // {
    //   href: "/gestion/messagerie",
    //   icon: ChatIcon,
    //   title: "Messagerie",
    //   badge: badgeCount > 0 ? badgeCount : null
    // },
    {
      icon: SettingsIcon,
      title: "Paramètres",
      permission: "settings_view",
      children: [
        {
          title: "Général",
          permission: "settings_view",
          children: [
            {
              href: "/settings/setting",
              title: "Paramètres",
              permission: "settings_view"
            },
            {
              href: "/settings/users",
              title: "Utilisateurs",
              permission: "isSuperAdmin = 1"
            },
            {
              href: "/settings/permission",
              title: "Permissions",
              permission: "isSuperAdmin = 1"
            },
            {
              href: "/settings/mail",
              title: "Mail",
              permission: "settings_view"
            },
          ]
        },
        {
          title: "Financier",
          permission: "settings_view",
          children: [
            {
              href: "/settings/modereglement",
              title: "Modes de Règlement",
              permission: "settings_view"
            },
            {
              href: "/settings/livraisonTerm",
              title: "Terme de Livraison",
              permission: "settings_view"
            },
            {
              href: "/settings/paiementTerm",
              title: "Terme de Paiement",
              permission: "settings_view"
            },
            {
              href: "/settings/typefacturation",
              title: "Type de Facturation",
              permission: "settings_view"
            },
            {
              href: "/settings/tax",
              title: "Taux de TVA",
              permission: "settings_view"
            },
            {
              href: "/settings/echeance",
              title: "Echéances",
              permission: "settings_view"
            },
            {
              href: "/settings/devise",
              title: "Devises",
              permission: "settings_view"
            },
          ]
        },
        {
          title: "Produits",
          permission: "settings_view",
          children: [
            {
              href: "/settings/operation",
              title: "Opérations",
              permission: "settings_view"
            },
            {
              href: "/settings/gamme",
              title: "Gammes",
              permission: "settings_view"
            },
            {
              href: "/settings/conditionnement/unit",
              title: "Unité Conditionnement",
              permission: "settings_view"
            },
            {
              href: "/settings/conditionnement/type",
              title: "Type Conditionnement",
              permission: "settings_view"
            },
            {
              href: "/settings/family/matiere",
              title: "Famille de Matière",
              permission: "settings_view"
            },
            {
              href: "/settings/family/article",
              title: "Famille d'Article",
              permission: "settings_view"
            },
          ]
        },
        {
          title: "Système",
          permission: "settings_view",
          children: [
            {
              href: "/settings/activity",
              title: "Activité",
              permission: "settings_view"
            },
            {
              href: "/settings/printer",
              title: "Imprimante",
              permission: "settings_view"
            },
            {
              href: "/settings/machine",
              title: "Machines",
              permission: "settings_view"
            },
          ]
        },
      ],
    },
    {
      href: "/gestion/quadratus",
      icon: FileUploadIcon,
      title: "Export Quadratus",
      permission: "quadratus_view"
    },
    {
      href: "/gestion/previsionnel",
      icon: ShowChartIcon,
      title: "Previsionnel",
      permission: "previsionnel_view"
    },
    // {
    //   href: "/gestion/trello",
    //   icon: WorkOutlineIcon,
    //   title: "Trello",
    //   permission: "previsionnel_view"
    // },
    {
      href: "/export",
      icon: ShowChartIcon,
      title: "Exports",
      children: [
        {
          href: "/export/Devis",
          title: "Devis",
          permission: "export_view",
        },
        {
          href: "/export/Commande",
          title: "Commande",
          permission: "export_view",
        },
        {
          href: "/export/Facture",
          title: "Facture",
          permission: "export_view",
        },
        {
          href: "/export/Achat",
          title: "Achat",
          permission: "export_view",
        },
        {
          href: "/export/Stock",
          title: "Stock",
          permission: "export_view",
        },
        {
          href: "/export/statscommandes",
          title: "Stats Commandes",
          permission: "export_view",
        },
      ],
    },
    ...(user?.id === 14 ? [{
      href: "/gestion/changelog",
      icon: CodeIcon,
      title: "Développeur",
      permission: "dev_access"
    }] : []),

  ];

  const navItems = [
    {
      title: "Pages",
      pages: pagesSection,
    },
  ];

  return navItems;
};

export default useDashboardItems;
